import { Injectable, inject } from '@angular/core';
import { Logger } from '@nsalaun/ng-logger';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { PageVisibilityState } from '../enums/page-visibility-state.enum';

export type PageVisibilityKeySet = {
	hidden: string;
	visibilitychange: string;
	visibilityState: string;
};

const PAGE_VISIBILITY_KEYS: PageVisibilityKeySet[] = [
	{
		// DEFAULT
		hidden: 'hidden',
		visibilitychange: 'visibilitychange',
		visibilityState: 'visibilityState',
	},
	{
		// MS
		hidden: 'msHidden',
		visibilitychange: 'msvisibilitychange',
		visibilityState: 'msVisibilityState',
	},
	{
		// WEBKIT
		hidden: 'webkitHidden',
		visibilitychange: 'webkitvisibilitychange',
		visibilityState: 'webkitVisibilityState',
	},
];

@Injectable({ providedIn: 'root' })
export class BrowserPageVisibilityApiService {
	private logger = inject(Logger);

	private eventAndStateKeys: any;
	private lastState: PageVisibilityState;
	private state$ = new BehaviorSubject<PageVisibilityState>(PageVisibilityState.visible);

	constructor() {
		this.init();
	}

	getReEnterState(): Observable<PageVisibilityState> {
		return this.state$
			.asObservable()
			.pipe(filter((state: PageVisibilityState) => state === PageVisibilityState.visible && this.lastState === PageVisibilityState.hidden));
	}

	private init(): void {
		this.eventAndStateKeys = this.setBrowserDependingKeys();
		if (!!this.eventAndStateKeys) {
			this.logger.debug('[BrowserPageVisibilityApiService] says setBrowserDependingKeys found: ', JSON.stringify(this.eventAndStateKeys));
			this.setEventListener();
		} else {
			this.logger.debug('[BrowserPageVisibilityApiService] says setBrowserDependingKeys failed');
		}
	}

	private setBrowserDependingKeys(): PageVisibilityKeySet | undefined {
		return PAGE_VISIBILITY_KEYS.find(
			(keySet: PageVisibilityKeySet) => (document as any)[keySet.hidden] === false || (document as any)[keySet.hidden] === true,
		);
	}

	private setEventListener(): void {
		document.addEventListener(this.eventAndStateKeys.visibilitychange, () => this.handleVisibilityChange(), false);
	}

	private handleVisibilityChange(): void {
		const visibilityState: PageVisibilityState = this.getVisibilityState();
		this.lastState = this.state$.value;
		this.state$.next(visibilityState);
	}

	private getVisibilityState(): PageVisibilityState {
		const apiState: string = (document as any)[this.eventAndStateKeys.visibilityState];
		// Ensure type safety by checking if apiState is a valid enum key
		if (Object.values(PageVisibilityState).includes(apiState as PageVisibilityState)) {
			return apiState as PageVisibilityState;
		}
		this.logger.warn(`[BrowserPageVisibilityApiService] Unknown visibility state: ${apiState}`);
		return PageVisibilityState.visible;
	}
}
